/**
 * MapPlaceLabel Component
 * Renders a place marker with optional label, heart icon, and link functionality
 */

import React, { useCallback, useEffect, useState } from 'react';
import classNames from 'classnames';
import { useHover, useWrappedTextWidth } from '../MapLabels/MapLabelsHooks';
import { HeartIcon } from '../MapLabels/MapLabelIcons';
import { useMapStateStore } from '../../../stores';
import './MapPlaceLabel.scss';

/**
 * Map Place Label Component
 * Memoized to prevent re-renders when props haven't changed
 */
const MapPlaceLabel = React.memo(({
  place,
  position,
  isLink,
  labelVisible,
  globalLabelVisible = true,
  onToggleLabel,
  onMarkerClick,
  uniqueId,
  isFavorited,
  showHeartEnabled,
  isPulsing = false,
  isPreviewMode = false,
}) => {
  const [isHovered, hoverHandlers] = useHover();
  const [isFocusSvg, setIsFocusSvg] = useState(false);

  const pin = place?.pin || {};
  const {
    svg_id: svgId,
    label_text: labelText = '',
    label_orientation: orientation = 'right',
    wrap_label: wrapLabel = true,
    unpublished = false,
  } = pin || {};

  const { labelRef, textRef, fittedWidth } = useWrappedTextWidth(labelText);
  const svgFocusId = useMapStateStore((state) => state.svgFocusId);
  // Hidden when a zoom-zone label covers this place and the overflow-cluster
  // fallback has taken over (see overflow flow in MapLabels).
  const isCovered = useMapStateStore((state) => state.coveredPlaceUuids.has(uniqueId));

  useEffect(() => {
    if (svgFocusId && svgId && (svgFocusId === svgId)) {
      setIsFocusSvg(svgId);
    } else {
      setIsFocusSvg(false);
    }
  }, [svgFocusId, svgId]);

  const showHeart = showHeartEnabled && isFavorited;

  const handleClick = useCallback((e) => {
    e.stopPropagation();
    isLink ? onMarkerClick?.(place) : onToggleLabel?.(uniqueId);
  }, [isLink, place, onMarkerClick, onToggleLabel, uniqueId]);

  // Hide entire marker when global toggle is off.
  if (!globalLabelVisible) {
    return null;
  }

  // Don't render pins/labels for unpublished places - only the SVG shape should show
  // In preview mode, show unpublished places as if they were published
  if (unpublished && !isPreviewMode) {
    return null;
  }

  const labelStyle = fittedWidth ? { width: `${fittedWidth}px` } : undefined;

  // When covered by a zoom-zone label's overflow, render with visibility
  // hidden instead of returning null. Keeps the marker's rect measurable
  // so the orientation pass can keep detecting the coverage on subsequent
  // runs — otherwise removing the DOM node makes the area look "clean"
  // and the zone exits overflow, the labels reappear, and we cycle.
  const markerClasses = classNames('htmlPlaceMarker', {
    'htmlPlaceMarker--hovered': isHovered && !unpublished,
    'htmlPlaceMarker--pulsing': !isLink,
    'htmlPlaceMarker--highlighted': isFocusSvg,
    'htmlPlaceMarker--covered': isCovered,
    'htmlPlaceMarker--unpublished': unpublished,
  });

  const labelClasses = classNames(
    'htmlPlaceLabel',
    `htmlPlaceLabel--${orientation}`,
    {
      'htmlPlaceLabel--visible': labelVisible,
      'htmlPlaceLabel--hidden': !labelVisible,
      'htmlPlaceLabel--hovered': isHovered,
      'htmlPlaceLabel--link': isLink,
      'htmlPlaceLabel--withHeart': showHeart,
      'htmlPlaceLabel--pulsing': isPulsing,
      'htmlPlaceLabel--noWrap': !wrapLabel,
    },
  );

  return (
    <div
      className={markerClasses}
      style={{ left: `${position.x}%`, top: `${position.y}%` }}
      onClick={handleClick}
      {...hoverHandlers}
    >
      <div className="htmlPlaceMarker__dot">
        {!isLink && <span className="htmlPlaceMarker__plusIcon" />}
      </div>

      {labelText && (isLink || labelVisible) && (
        <div
          className={labelClasses}
          ref={labelRef}
          style={labelStyle}
          data-place-uuid={uniqueId}
        >
          {showHeart && <HeartIcon className="htmlPlaceLabel__heart" isActive />}
          <span className="htmlPlaceLabel__text" ref={textRef}>{labelText}</span>
          {isLink && (
            <>
              <span className="htmlPlaceLabel__divider" />
              <span className="htmlPlaceLabel__plus" />
            </>
          )}
        </div>
      )}
    </div>
  );
});

export default MapPlaceLabel;
